import { ConversationStatus } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { Artifact, Conversation, Message } from '../types';

// ============================================================================
// PAGINATION UTILITIES
// ============================================================================

/**
 * Generic pagination utility to handle continuationToken logic
 * @param apiCall Function that makes the API call with optional continuation token
 * @param initialToken Optional initial continuation token
 * @returns Promise that resolves to aggregated data from all pages
 */
export async function fetchAllPages<T>(
    apiCall: (token?: string) => Promise<{ data: T[]; continuationToken?: string }>,
    initialToken?: string
): Promise<T[]> {
    const allData: T[] = [];
    let currentToken = initialToken;
    let hasMorePages = true;

    while (hasMorePages) {
        try {
            const response = await apiCall(currentToken);
            allData.push(...response.data);

            if (response.continuationToken) {
                currentToken = response.continuationToken;
            } else {
                hasMorePages = false;
            }
        } catch (error) {
            // Log error but return partial data if we have any
            console.error('Pagination error:', error);
            if (allData.length > 0) {
                console.warn('Returning partial data due to pagination error');
                break;
            }
            throw error;
        }
    }

    return allData;
}

// ============================================================================
// TIMESTAMP UTILITIES
// ============================================================================

/**
 * Convert timestamp to number for comparison and math operations
 * Handles both string and number timestamps
 * @param timestamp Timestamp as string or number
 * @returns Timestamp as number
 */
export function timestampToNumber(timestamp: string | number): number {
    return typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
}

/**
 * Compare two timestamps
 * @param a First timestamp
 * @param b Second timestamp
 * @returns Comparison result (-1, 0, 1)
 */
export function compareTimestamps(a: string | number, b: string | number): number {
    const numA = timestampToNumber(a);
    const numB = timestampToNumber(b);
    return numA - numB;
}

/**
 * Get the maximum timestamp from an array
 * @param timestamps Array of timestamps
 * @returns Maximum timestamp as number
 */
export function getMaxTimestamp(timestamps: (string | number)[]): number {
    return Math.max(...timestamps.map(timestampToNumber));
}

/**
 * Format timestamp for display
 * @param timestamp Timestamp as string or number (nanoseconds)
 * @returns Formatted time string
 */
export function formatTimestamp(timestamp: string | number): string {
    const timestampNum = timestampToNumber(timestamp);
    const date = new Date(timestampNum / 1000000); // Convert from nanoseconds to milliseconds
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ============================================================================
// MESSAGE VALIDATION UTILITIES
// ============================================================================

/**
 * Check if a message is from a user
 */
export const isUserMessage = (message: Message): boolean => {
    return message.messageType === 'user';
};

/**
 * Check if a message is an error
 */
export const isErrorMessage = (message: Message): boolean => {
    return message.messageType === 'error';
};

/**
 * Check if a message is from AI (system or tool)
 */
export const isAIMessage = (message: Message): boolean => {
    return message.messageType === 'system' || message.messageType === 'tool';
};

/**
 * Check if a message is a tool message
 */
export const isToolMessage = (message: Message): boolean => {
    return message.messageType === 'tool';
};

/**
 * Check if a message has tool usage information
 */
export const hasToolUsage = (message: Message): boolean => {
    return message.toolUsage !== undefined;
};

/**
 * Parse artifact content from JSON or return as text
 */
export const parseArtifactContent = (artifact: Artifact): any => {
    try {
        return JSON.parse(artifact.objectContent);
    } catch (error) {
        return { text: artifact.objectContent };
    }
};

// ============================================================================
// MESSAGE GROUPING UTILITIES
// ============================================================================

export interface MessageInteraction {
    userMessage: Message;
    intermediateMessages: Message[]; // system/tool messages
    finalAnswer?: Message; // Optional - might not exist for ongoing conversations
    isComplete: boolean;
}

const SYSTEM_MESSAGES = new Set(['system', 'tool', 'error']);

/**
 * Group messages into user interactions for collapsible display
 * Each interaction consists of: user message → intermediate messages → final answer
 */
export const groupMessagesIntoInteractions = (
    messages: Message[],
    conversationStatus: string
): MessageInteraction[] => {
    const interactions: MessageInteraction[] = [];
    let currentInteraction: Partial<MessageInteraction> | null = null;

    for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        const nextMessage = messages[i + 1];

        if (message.messageType === 'user') {
            // Start new interaction
            currentInteraction = {
                userMessage: message,
                intermediateMessages: [],
                isComplete: false
            };
        } else if (currentInteraction && SYSTEM_MESSAGES.has(message.messageType)) {
            // Add to intermediate messages
            if (currentInteraction.intermediateMessages) {
                currentInteraction.intermediateMessages.push(message);
            }

            // Check if this is the final answer
            const isLastMessage = i === messages.length - 1;
            const nextIsUser = nextMessage && nextMessage.messageType === 'user';
            const conversationComplete = [
                ConversationStatus.READY,
                ConversationStatus.CLOSED,
                ConversationStatus.ERROR
            ].includes(conversationStatus as ConversationStatus);

            if (nextIsUser || (isLastMessage && conversationComplete)) {
                // This is the final answer for this interaction
                currentInteraction.finalAnswer = message;
                currentInteraction.isComplete = true;

                // Remove from intermediate messages since it's the final answer
                if (currentInteraction.intermediateMessages) {
                    currentInteraction.intermediateMessages = currentInteraction.intermediateMessages.slice(0, -1);
                }

                interactions.push(currentInteraction as MessageInteraction);
                currentInteraction = null;
            }
        }
    }

    // Handle incomplete interaction (ongoing conversation)
    if (currentInteraction) {
        interactions.push(currentInteraction as MessageInteraction);
    }

    return interactions;
};
// ============================================================================
// DATE GROUPING UTILITIES
// ============================================================================

/**
 * Check if two dates are in the same week
 */
export const isSameWeek = (date1: Date, date2: Date): boolean => {
    const startOfWeek = (date: Date) => {
        const d = new Date(date);
        const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as start of week
        return new Date(d.setDate(diff));
    };

    const week1Start = startOfWeek(date1);
    const week2Start = startOfWeek(date2);

    return week1Start.getTime() === week2Start.getTime();
};

/**
 * Check if two dates are in the same month
 */
export const isSameMonth = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth();
};

/**
 * Check if two dates are in the same year
 */
export const isSameYear = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear();
};

/**
 * Get the date group for a conversation timestamp
 * @param timestamp Timestamp as string (nanoseconds)
 * @returns Date group string
 */
export const getDateGroup = (timestamp: string): string => {
    const date = new Date(parseInt(timestamp) / 1000000); // Convert from nanoseconds to milliseconds
    const now = new Date();

    // Reset time to start of day for accurate comparison
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffInDays = Math.floor((nowStart.getTime() - dateStart.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
        return 'Today';
    } else if (diffInDays === 1) {
        return 'Yesterday';
    } else if (diffInDays <= 7 && isSameWeek(date, now)) {
        return 'This Week';
    } else if (isSameMonth(date, now)) {
        return 'This Month';
    } else if (isSameYear(date, now)) {
        return 'This Year';
    } else {
        return 'Earlier';
    }
};

/**
 * Group conversations by date categories
 * @param conversations Array of conversations to group
 * @returns Record of date groups with conversations
 */
export const groupConversationsByDate = (conversations: Conversation[]): Record<string, Conversation[]> => {
    const groups: Record<string, Conversation[]> = {};
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'This Year', 'Earlier'];

    conversations.forEach((conversation) => {
        const group = getDateGroup(conversation.timestamp);
        if (!groups[group]) {
            groups[group] = [];
        }
        groups[group].push(conversation);
    });

    // Sort conversations within each group by timestamp (newest first)
    Object.keys(groups).forEach((group) => {
        groups[group].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp));
    });

    // Return groups in the correct order
    const orderedGroups: Record<string, Conversation[]> = {};
    groupOrder.forEach((group) => {
        if (groups[group]) {
            orderedGroups[group] = groups[group];
        }
    });

    return orderedGroups;
};

// ============================================================================
// TABLE UTILITIES
// ============================================================================

/**
 * Check if all objects in the array have the same set of keys
 */
export function hasConsistentSchema(jsonArray: any[]): boolean {
    if (!Array.isArray(jsonArray) || jsonArray.length === 0) {
        return false;
    }

    // Get keys from the first object
    const firstObject = jsonArray[0];
    if (!firstObject || typeof firstObject !== 'object') {
        return false;
    }

    const firstKeys = Object.keys(firstObject).sort();

    // Check if all other objects have the same keys
    for (let i = 1; i < jsonArray.length; i++) {
        const currentObject = jsonArray[i];
        if (!currentObject || typeof currentObject !== 'object') {
            return false;
        }

        const currentKeys = Object.keys(currentObject).sort();

        // Compare key arrays
        if (firstKeys.length !== currentKeys.length) {
            return false;
        }

        for (let j = 0; j < firstKeys.length; j++) {
            if (firstKeys[j] !== currentKeys[j]) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Generate basic AG-Grid column definitions from object keys
 */
export function generateColumnDefs(firstObject: any): ColDef[] {
    if (!firstObject || typeof firstObject !== 'object') {
        return [];
    }

    const keys = Object.keys(firstObject);

    return keys.map((key) => ({
        field: key,
        headerName: key,
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 120,
        valueFormatter: (params: ValueFormatterParams) => {
            // Handle arrays and objects by stringifying them
            if (params.value === null || params.value === undefined) {
                return '';
            }
            if (typeof params.value === 'object') {
                return JSON.stringify(params.value);
            }
            return params.value;
        }
    }));
}
